import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { OverlayAlertView } from '../components/OverlayAlertView';
import { overlayStreamUrl, type OverlayAlert, type OverlayTheme } from '../services/api';

/**
 * Página do OVERLAY exibida DENTRO do OBS (Browser Source).
 *
 * - Fundo 100% transparente: só o alerta aparece por cima do vídeo da live.
 * - Conecta no fluxo SSE da API e exibe cada alerta pelo tempo configurado
 *   na dashboard, um por vez (os demais aguardam em fila).
 * - Aparência e POSIÇÃO vêm do tema configurado na dashboard: cada alerta
 *   carrega o tema fresco, então mudanças valem sem reconectar o OBS.
 * - O EventSource reconecta sozinho se a conexão cair.
 */
export function OverlayPage() {
  const { token = '' } = useParams();
  const [current, setCurrent] = useState<OverlayAlert | null>(null);
  const [theme, setTheme] = useState<OverlayTheme | null>(null);

  // Fila de alertas pendentes (refs para não recriar a conexão SSE a cada estado)
  const queueRef = useRef<OverlayAlert[]>([]);
  const busyRef = useRef(false);
  const timersRef = useRef<number[]>([]);

  // Fundo transparente no <body> enquanto o overlay estiver montado
  useEffect(() => {
    document.body.classList.add('overlay-body');
    return () => document.body.classList.remove('overlay-body');
  }, []);

  useEffect(() => {
    const source = new EventSource(overlayStreamUrl(token));

    function showNext() {
      const next = queueRef.current.shift();
      if (!next) {
        busyRef.current = false;
        return;
      }
      busyRef.current = true;
      if (next.theme) setTheme(next.theme);
      setCurrent(next);

      // Esconde após a duração configurada e abre espaço para o próximo
      // (pequena pausa entre alertas para a animação de saída respirar)
      timersRef.current.push(
        window.setTimeout(() => {
          setCurrent(null);
          timersRef.current.push(window.setTimeout(showNext, 400));
        }, next.durationSeconds * 1000),
      );
    }

    // Configurações iniciais (inclui o tema, para a posição já ficar certa)
    source.addEventListener('settings', (event) => {
      const settings = JSON.parse((event as MessageEvent).data) as { theme?: OverlayTheme };
      if (settings.theme) setTheme(settings.theme);
    });

    source.addEventListener('alert', (event) => {
      const alert = JSON.parse((event as MessageEvent).data) as OverlayAlert;
      queueRef.current.push(alert);
      if (!busyRef.current) showNext();
    });

    return () => {
      source.close();
      timersRef.current.forEach((timer) => window.clearTimeout(timer));
      timersRef.current = [];
    };
  }, [token]);

  const position = theme?.position ?? 'bottom-center';

  return (
    <div className={`overlay overlay--${position}`}>
      {current && theme && (
        <div className="overlay__alert-wrapper" key={current.id}>
          <OverlayAlertView theme={theme} alert={current} />
        </div>
      )}
    </div>
  );
}
